import { CardAPI, computed, debug, NetbankingAPI, observable, toJS } from "aaa";
import { CardAPIClient, NetbankingAPIClient } from "network/APIClient";
import { AccountsStore } from "stores/AccountsStore";
import { ContactsStore } from "stores/ContactsStore";
import { isNewCreditCard, OffersStore } from "stores/OffersStore";
import profileStore from "stores/ProfileStore";
import * as amount from "types/amount";
import { Amount } from "types/amount";
import { IBankCardOfferDetails, ICreditCardMonthlyBillType, ICreditCardOfferDetails, ICreditCardType, ICreditCardVariant, IOffer } from "types/offer";
import { ui } from "components/util/uiHelpers";

const MILES_AND_MORE_ABBREVIATION = "M&M";
interface IConfig {
    loadIdentification?: boolean;
    loadAccounts?: boolean;
    loadProfile?: boolean;
    loadContacts?: boolean;

    // Filters which accounts to load
    accountsUsableForQuery?: string;
    accountsUsableForFilter?: string;

    // Filter which postal addresses to load
    addressFilter?: string;
}

export class ShopStore {
    @observable offersStore: OffersStore;
    @observable offer?: IOffer;
    @observable offerDetails?: ICreditCardOfferDetails | IBankCardOfferDetails;
    @observable cardFormFactor: ICreditCardType = "physical";
    @observable cardCharacteristic: ICreditCardVariant = "regular";
    @observable monthlyBillType: ICreditCardMonthlyBillType = "postal";
    @observable monthlyBillAddressId?: string;

    @observable repaymentChargeCard: boolean = true;
    @observable revolvingPercentage = 0;
    @observable revolvingMinRate = 0;

    @observable isLoadingPurchase = false;

    @computed get isLoading() {
        return this.isLoadingOffer || this.isLoadingUserInfo || this.isLoadingPurchase || this.isLoadingAddresses;
    }

    @observable accountsStore: AccountsStore;

    @observable contactsStore: ContactsStore;
    @observable contractEmailId?: string;

    @observable isLoadingOffer: boolean = false;
    @observable isLoadingUserInfo: boolean = false;
    @observable isLoadingCardLimits: boolean = false;
    @observable isLoadingAddresses: boolean = false;

    @observable amount: Amount = new Amount(amount.ZERO_AMOUNT);
    @computed get amountString(): string {
        return this.amount.currencyString;
    }

    @computed get isMilesAndMoreCreditCard() {
        return this.offerDetails?.milesAndMoreSupported ?? false;
    }

    @computed get productName() {
        if (!this.offer) {
            return "";
        }

        if (this.isMilesAndMoreCreditCard && this.offer.productName.includes(MILES_AND_MORE_ABBREVIATION)) {
            return this.offer.productName.replace(MILES_AND_MORE_ABBREVIATION, ui.__("shop.cardInfo.milesAndMore.replacementText"));
        }

        return this.offer.productName;
    }

    private config: IConfig;

    constructor(config: IConfig) {
        this.config = config;

        this.offersStore = new OffersStore();
        this.accountsStore = new AccountsStore();
        this.contactsStore = new ContactsStore();

    }

    protected onOfferReceived() {
        // Override in child class, to handle stuff after offer was received
    }

    async loadOffer(productId: string, conditionsGroupId: string, channelProduct: string, isBusinessInRetail?: boolean) {
        this.isLoadingOffer = true;

        try {
            await this.offersStore.load();

            this.offer = this.offersStore.findByProductIdAndConditionsGroup(productId, conditionsGroupId, channelProduct);

            debug.log("### offer", toJS(this.offer));

            if (this.offer) {
                this.onOfferReceived();

                if (isNewCreditCard(channelProduct) || channelProduct === "C-SKMCVORSCH" || isBusinessInRetail) {
                    const target = CardAPI.getOfferDetails(this.offer);
                    this.offerDetails = await CardAPIClient.requestType(target);
                } else {
                    const target = NetbankingAPI.getOfferDetails(this.offer);
                    this.offerDetails = await NetbankingAPIClient.requestType(target);
                }

                debug.log("### offerDetails", toJS(this.offerDetails));

                if (this.offerDetails && !(this.offerDetails as ICreditCardOfferDetails).virtualCardSupported) {
                    this.cardFormFactor = "physical";
                }

                if (this.offerDetails && !(this.offerDetails as ICreditCardOfferDetails).zeroLimitCardSupported) {
                    this.cardCharacteristic = "regular";
                }
            } else {
                throw new Error("### offer not found");
            }

            this.isLoadingOffer = false;
        } catch (error) {
            this.isLoadingOffer = false;
            throw error;
        }
    }

    async loadUserInfo() {
        debug.log("### load user info");

        this.isLoadingUserInfo = true;

        try {
            const promises = [];

            if (this.config.loadContacts) {
                promises.push(this.contactsStore.load(this.config.addressFilter));
            }

            if (this.config.loadIdentification) {
                promises.push(profileStore.loadIdentification());
            }

            if (this.config.loadAccounts) {
                promises.push(this.accountsStore.load(this.config.accountsUsableForQuery, this.config.accountsUsableForFilter));
            }

            await Promise.all(promises);

            this.isLoadingUserInfo = false;
        } catch (err) {
            this.isLoadingUserInfo = false;
            throw err;
        }

        debug.log("### accounts", toJS(this.accountsStore.accounts));
        debug.log("### contacts", toJS(this.contactsStore.contacts));
    }

    async load(productId: string, conditionsGroupId: string, channelProduct: string, isBusinessInRetail?: boolean) {
        debug.log("### ShopStore load");

        try {
            await Promise.all([
                this.loadUserInfo(),
                this.loadOffer(productId, conditionsGroupId, channelProduct, isBusinessInRetail),
            ]);
        } catch (error) {
            throw error;
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/shop/ShopStore.ts